import { OverlayPortal as FloatingPortal } from "@/components/modal/OverlayPortal";
import { Card } from "@/components/Card";
import { Icon, IconName } from "@/components/icons/Icon";
import { IconButton } from "@/components/interactive/IconButton";
import { ZIndex, ZIndexClass } from "@/constants/z-index";
import { classNames } from "@/utils/classnames";
import {
  Props as FloatingProps,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom-interactions";
import { Menu, Transition, MenuItem, MenuButton, MenuItems } from "@headlessui/react";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import React, { ComponentProps, Fragment, ReactNode, Ref, useContext, useEffect } from "react";

const DropdownContext = React.createContext({ compact: false });

interface DropdownProps {
  children?: ReactNode;
  renderButton?: (ref: Ref<HTMLButtonElement>, open: boolean) => ReactNode;
  floatOptions?: Partial<FloatingProps & { offset: Parameters<typeof offset>[0] }>;
  cardProps?: ComponentProps<typeof Card>;
  compact?: boolean;
  className?: string;
  zIndex?: number;
  disabled?: boolean;
}

export function DropdownMenu({ children, compact, ...props }: DropdownProps) {
  return (
    <DropdownContext.Provider value={{ compact: Boolean(compact) }}>
      <Menu>
        {({ open }) => (
          <DropdownMenuInside {...props} open={open}>
            {children}
          </DropdownMenuInside>
        )}
      </Menu>
    </DropdownContext.Provider>
  );
}

// Needs to be a separate class so that Menu can manage the open state I guess
function DropdownMenuInside({
  children,
  renderButton = (r) => <IconButton ref={r} iconName={"more"} />,
  open,
  floatOptions = {},
  cardProps,
  className,
  zIndex = ZIndex.POPOVER,
  disabled,
}: DropdownProps & {
  open: boolean;
}) {
  const { offset: offsetOptions, ...options } = floatOptions;
  const float = useFloating({
    open,
    strategy: "absolute",
    middleware: [offset(offsetOptions ?? 8), flip({ padding: 24 }), shift({ padding: 24 })],
    ...options,
  });

  useEffect(() => {
    if (float.refs.reference.current && float.refs.floating.current && open) {
      return autoUpdate(float.refs.reference.current, float.refs.floating.current, float.update);
    }
  }, [float.refs.reference, float.refs.floating, float.update, open]);

  return (
    <div className={className}>
      <MenuButton as={"div"} disabled={disabled}>
        {renderButton(float.reference, open)}
      </MenuButton>
      <FloatingPortal>
        <div
          ref={float.floating}
          style={{
            position: float.strategy,
            left: float.x ?? 20,
            top: float.y ?? 100,
            zIndex,
          }}
          className={classNames(ZIndexClass.POPOVER, "design-system-typography")}
        >
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-90"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-90"
          >
            <MenuItems>
              <Card
                {...cardProps}
                className={classNames(
                  "design-system-typography p-0 !m-0 flex flex-col justify-start items-stretch overflow-hidden shadow-lg rounded-sm",
                  cardProps?.className,
                )}
              >
                {children}
              </Card>
            </MenuItems>
          </Transition>
        </div>
      </FloatingPortal>
    </div>
  );
}

export interface DropdownMenuItemProps
  extends Omit<React.ComponentProps<"a">, "ref">,
    React.RefAttributes<HTMLAnchorElement> {
  icon?: IconName;
  iconClassName?: string;
  current?: boolean;
  disabled?: boolean;
  /**
   * Destructive action: colors the label and its icon together. Setting `className` alone
   * leaves the icon zinc, which reads as a half-styled row.
   */
  destructive?: boolean;
}

export function DropdownMenuItem({
  children,
  icon,
  iconClassName,
  className,
  current,
  disabled,
  destructive,
  href,
  ...rest
}: DropdownMenuItemProps) {
  const { compact } = useContext(DropdownContext);
  const destructiveClass = destructive ? "!text-cds-error" : undefined;
  return (
    <MenuItem disabled={disabled}>
      {({ active, disabled }) =>
        href ? (
          <ChalkLink
            unstyled
            className={classNames(
              "flex w-full gap-1 items-center text-xs text-zinc-700 text-left",
              compact ? "px-2.5 py-1.5" : "pl-2 pr-3 py-2.5",
              disabled ? "" : "cursor-pointer hover:bg-zinc-100 active:bg-zinc-200",
              active ? "bg-zinc-100" : "",
              current ? "bg-zinc-50" : "",
              destructiveClass,
              className,
            )}
            href={href}
            {...rest}
          >
            {icon ? (
              <span className="inline-block">
                <Icon
                  name={icon}
                  className={classNames("text-zinc-500", destructiveClass, iconClassName)}
                />
              </span>
            ) : null}
            {children}
          </ChalkLink>
        ) : (
          <a
            className={classNames(
              "flex w-full gap-1.5 items-center text-xs text-zinc-700 text-left",
              compact ? "px-2.5 py-1.5" : "pl-2 pr-3 py-2.5",
              disabled ? "" : "cursor-pointer hover:bg-zinc-100 active:bg-zinc-200",
              active ? "bg-zinc-100" : "",
              current ? "bg-zinc-50" : "",
              destructiveClass,
              className,
            )}
            href={href}
            {...rest}
          >
            {icon ? (
              <span className="inline-block">
                <Icon
                  name={icon}
                  className={classNames("text-zinc-500", destructiveClass, iconClassName)}
                />
              </span>
            ) : null}
            {children}
          </a>
        )
      }
    </MenuItem>
  );
}
